import React from "react";
import { DatePicker, Form, Input, Radio } from "antd";
import { useGetIsPhone } from "@extensions";
import { clientProfileFields } from "@extensions/enums";
import "./profile-fields.css";

// Признак обязательного поля для анкеты клиента
export const FIELD_NAME     = "name";
export const FIELD_BIRTHDAY = "dateBirth";
export const FIELD_SEX      = "sex";
export const FIELD_EMAIL    = "email";


// Обязательные поля анкеты по настройкам компании.
// Если форма анкеты не требуется - обязательных полей нет.
export const getRequiredProfileFields = (company) => {
  const flags = company?.requireFormForWidget ? (company.requiredClientProfileFields || 0) : 0;
  return {
    [FIELD_NAME]:     (flags & clientProfileFields.Name)      !== 0,
    [FIELD_BIRTHDAY]: (flags & clientProfileFields.Birthday)  !== 0,
    [FIELD_SEX]:      (flags & clientProfileFields.Sex)       !== 0,
    [FIELD_EMAIL]:    (flags & clientProfileFields.Email)     !== 0,
  };
};


// Список незаполненных обязательных полей анкеты клиента.
// Используется для проверки участников группы перед покупкой и в самой анкете.
export const getMissingClientFields = (client, company) => {
  const required = getRequiredProfileFields(company);
  const missing = [];
  if (required[FIELD_NAME]) {
    const name = client?.name || "";
    if (name.trim() === "" || /^\+?\d+$/.test(name.trim())) {
      missing.push("ФИО");
    }
  }
  if (required[FIELD_BIRTHDAY] && !client?.dateBirth) {
    missing.push("Дата рождения");
  }
  if (required[FIELD_SEX] && (client?.sex === null || client?.sex === undefined)) {
    missing.push("Пол");
  }
  if (required[FIELD_EMAIL] && String(client?.email || "").trim() === "") {
    missing.push("Почта");
  }
  return missing;
};

// Правила валидации поля анкеты по настройкам компании
const fieldRules = (company, field) => {
  const required = getRequiredProfileFields(company);
  switch (field) {
    case FIELD_NAME:
      return required[FIELD_NAME] ? [{ required: true, message: "Пожалуйста, введите ваше имя" }] : [];
    case FIELD_BIRTHDAY:
      return required[FIELD_BIRTHDAY] ? [{ type: "object", required: true, message: "Пожалуйста, выберите дату рождения" }] : [];
    case FIELD_SEX:
      return required[FIELD_SEX] ? [{ required: true, message: "Пожалуйста, укажите ваш пол" }] : [];
    case FIELD_EMAIL:
      return required[FIELD_EMAIL] ? [{ type: "email", required: true, message: "Пожалуйста, введите ваш email" }] : [];
    default:
      return [];
  }
};

// Поля анкеты клиента - единые для страницы профиля и модалки участника группы,
// чтобы внешний вид, порядок полей и подписи совпадали.
// - Значения 0/1 - пол (0 женский, 1 мужской), null - не указан.
// - qr - блок QR-кода, на телефоне он выводится под полями.
// - emailEditable - почту можно вводить; по умолчанию она только для чтения.
// - emailActions - дополнительные элементы (кнопка "Получить код", поле кода) справа от поля почты.
// - showEmailInput - показывать ли само поле ввода почты (скрываем во время ввода кода подтверждения).
const EmailField = ({ value, onChange, disabled, showEmailInput, emailActions }) => {
  if (!showEmailInput) {
    // Во время ввода кода подтверждения Input скрыт, отображаются только действия справа.
    return <>{emailActions}</>;
  }
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <Input value={value ?? ""} onChange={onChange} disabled={disabled} style={{ flex: 1 }} />
      {emailActions}
    </div>
  );
};

const ProfileFields = ({ company, phone, emailEditable, nameDisabled, autopayCard, qr, emailActions, showEmailInput }) => {
  const isPhone = useGetIsPhone();
  // Правила валидации: для поля почты правило применяется только когда поле редактируемо
  const rulesFor = (field) => {
    if (field === FIELD_EMAIL && !emailEditable) {
      return [];
    }
    return fieldRules(company, field);
  };

  const fields = (
    <>
      <Form.Item label="ФИО" name={FIELD_NAME} validateTrigger={["onBlur", "onChange"]} rules={rulesFor(FIELD_NAME)}>
        <Input disabled={nameDisabled} maxLength={100} />
      </Form.Item>
      <Form.Item label="Телефон">{phone || "—"}</Form.Item>
      {autopayCard && (
        <>
          <Form.Item label="Автопродление">
            <Input size="middle" name="autopayCard" disabled value={autopayCard} />
          </Form.Item>
        </>
      )}
      <Form.Item label="Дата рождения" name={FIELD_BIRTHDAY} validateTrigger={["onBlur", "onChange"]} rules={rulesFor(FIELD_BIRTHDAY)}>
        <DatePicker format="DD.MM.YYYY" />
      </Form.Item>
      <Form.Item
        label="Почта"
        name={FIELD_EMAIL}
        required={getRequiredProfileFields(company)[FIELD_EMAIL]}
        validateTrigger={["onBlur", "onChange"]}
        rules={showEmailInput ? rulesFor(FIELD_EMAIL) : []}
      >
        <EmailField disabled={!emailEditable} showEmailInput={showEmailInput} emailActions={emailActions} />
      </Form.Item>
      <Form.Item label="Пол" name={FIELD_SEX} validateTrigger={["onBlur", "onChange"]} rules={fieldRules(company, FIELD_SEX)}>
        <Radio.Group>
          {getRequiredProfileFields(company)[FIELD_SEX] ? null : (
            <Radio.Button value={null}>Не указано</Radio.Button>
          )}
          <Radio.Button value={1}>Мужской</Radio.Button>
          <Radio.Button value={0}>Женский</Radio.Button>
        </Radio.Group>
      </Form.Item>
    </>
  );

  if (isPhone) {
    return (
      <div className="profile-fields-stack">
        {fields}
        {qr}
      </div>
    );
  }
  return (
    <>
      {fields}
      {qr}
    </>
  );
};

export default ProfileFields;
